import { useEffect, useState } from 'react'
import { WorkedAtDto } from '../../api/person/models/WorkedAtDto'
import { formatDuration } from '../../utils/dateUtils'
import ExpandableText from '../ExpandableText'
import { formatText, getSortedWorkedAts } from '../../utils/personUtils'

interface GroupedJob {
    companyId: string
    companyName: string
    companyLogo: string
    roles: WorkedAtDto[]
}

const FALLBACK_COMPANY_LOGO = '/no-company-logo.png'

const getCompanyLogoUrl = (job: WorkedAtDto): string => {
    const logoUrl = job.company?.companyLogoUrl?.trim()
    return logoUrl || FALLBACK_COMPANY_LOGO
}

const groupJobsByCompany = (jobs: WorkedAtDto[]): GroupedJob[] => {
    const sortedJobs = getSortedWorkedAts(jobs)

    const grouped: GroupedJob[] = []
    sortedJobs.forEach((job) => {
        const lastGroup = grouped[grouped.length - 1]
        const companyLogo = getCompanyLogoUrl(job)
        if (lastGroup && lastGroup.companyId === job.companyId) {
            lastGroup.roles.push(job)
            if (
                lastGroup.companyLogo === FALLBACK_COMPANY_LOGO &&
                companyLogo !== FALLBACK_COMPANY_LOGO
            ) {
                lastGroup.companyLogo = companyLogo
            }
        } else {
            grouped.push({
                companyId: job.companyId,
                companyName: job.company?.name ?? 'Unknown Company',
                companyLogo,
                roles: [job],
            })
        }
    })
    return grouped
}

const HelixJobTimeline = ({ jobs }: { jobs: WorkedAtDto[] }) => {
    const [groupedJobs, setGroupedJobs] = useState<GroupedJob[]>([])

    useEffect(() => {
        setGroupedJobs(groupJobsByCompany([...jobs]))
    }, [jobs])

    return (
        <div className="timeline-container w-full px-4 mx-auto pb-4">
            <h2 className="text-lg font-semibold mb-2">Experience</h2>

            <div className="mt-2">
                {groupedJobs.map((group, index) =>
                    group.roles.length === 1 ? (
                        <div key={index}>
                            {/* Single Role Format */}
                            <div className="flex gap-4">
                                <div className="logo-container w-12 h-12 flex-shrink-0">
                                    <img
                                        src={group.companyLogo}
                                        alt={group.companyName}
                                        className="w-full h-full object-cover"
                                        width={48}
                                        height={48}
                                        onError={(e) => {
                                            e.currentTarget.onerror = null
                                            e.currentTarget.src =
                                                FALLBACK_COMPANY_LOGO
                                        }}
                                    />
                                </div>
                                <div>
                                    <h4 className="font-bold text-base">
                                        {group.roles[0].jobTitle}
                                    </h4>
                                    <div className="text-sm text-gray-600">
                                        {group.companyName}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {
                                            formatDuration(
                                                group.roles[0].startDate,
                                                group.roles[0].endDate,
                                            ).fromTo
                                        }
                                        <span> · </span>
                                        {
                                            formatDuration(
                                                group.roles[0].startDate,
                                                group.roles[0].endDate,
                                            ).duration
                                        }
                                    </div>
                                    <div>
                                        <ExpandableText
                                            className="text-sm whitespace-pre-line text-gray-600 mt-2"
                                            text={formatText(
                                                group.roles[0].description ??
                                                    '',
                                            )}
                                            maxLines={2}
                                        />
                                    </div>
                                </div>
                            </div>
                            {index !== groupedJobs.length - 1 && (
                                <div className="w-full border-b border-gray-200 my-4"></div>
                            )}
                        </div>
                    ) : (
                        <div key={index} className="company-group mb-4">
                            {/* Multiple Roles Format */}
                            <div className="flex items-center mb-4">
                                <div className="logo-container w-12 h-12 flex-shrink-0 mr-4">
                                    <img
                                        src={group.companyLogo}
                                        alt={group.companyName}
                                        className="w-full h-full object-cover"
                                        width={48}
                                        height={48}
                                        onError={(e) => {
                                            e.currentTarget.onerror = null
                                            e.currentTarget.src =
                                                FALLBACK_COMPANY_LOGO
                                        }}
                                    />
                                </div>
                                <div>
                                    <h3 className="font-bold text-base">
                                        <span className="text-[1.1rem] font-bold break-words">
                                            {group.companyName}
                                        </span>
                                    </h3>
                                    <div className="text-sm text-gray-500">
                                        {formatTotalDuration(group.roles)}
                                    </div>
                                </div>
                            </div>
                            {/* Role List */}
                            <div className="role-list ml-[22px] border-l-2 border-gray-300 relative">
                                {group.roles.map(
                                    (role: WorkedAtDto, roleIndex: number) => {
                                        const overlapDuration = formatDuration(
                                            role.startDate,
                                            role.endDate,
                                        )
                                        return (
                                            <div
                                                key={roleIndex}
                                                className="relative mb-4 pl-6"
                                            >
                                                {/* Dot perfectly centered on the vertical line */}
                                                <div className="absolute left-[-5px] top-2 w-2 h-2 rounded-full bg-gray-300" />

                                                <h4 className="text-base font-bold">
                                                    {role.jobTitle}
                                                </h4>
                                                <p className="text-sm text-gray-500">
                                                    {overlapDuration.fromTo}
                                                    <span> · </span>
                                                    {overlapDuration.duration}
                                                </p>

                                                <ExpandableText
                                                    className="text-sm whitespace-pre-line text-gray-600"
                                                    text={formatText(
                                                        role.description ?? '',
                                                    )}
                                                    maxLines={2}
                                                />
                                            </div>
                                        )
                                    },
                                )}
                            </div>
                            {index !== groupedJobs.length - 1 && (
                                <div className="w-full border-b border-gray-200 my-4"></div>
                            )}
                        </div>
                    ),
                )}
            </div>
        </div>
    )
}

const formatTotalDuration = (roles: string | any[]) => {
    if (!roles.length) return ''
    const firstStartDate = roles[roles.length - 1].startDate
    const lastEndDate = roles[0].endDate || new Date().toISOString()
    const totalDuration = formatDuration(firstStartDate, lastEndDate)
    return totalDuration.duration
}

export default HelixJobTimeline
